import React, { useState, useRef, useEffect, useCallback } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import Lottie from 'lottie-react';
import Skeleton from '@mui/material/Skeleton';
import {
	SearchIconGold,
	PlusGold,
	TrashNew,
	ExclamationMarkRed,
	ExclamationMarkBlack,
	DropDownGold,
	CheckmarkIcon,
	CheckmarkIconBlack,
	Checkmark,
	XIcon,
	XIconBlack,
	ActivityListEmpty,
	Compose,
	Megaphone
} from '@/assets/images';
import type { RootState } from '@/rootReducer';
import { sendAnalytics } from '@/services/analyticsService';
import {
	dashboardUpdateBusinessNotesRequest,
	dashboardGetActivitiesRequest,
	dashboardCreateActivityNote,
	dashboardCreateActivityNoteClear,
	dashboardActivitySearch,
	dashboardGetBusinessCloseNote,
	dashboardClickDeleteNoteClear
} from '@/modules/dashboard/dashboardActions';
import type {
	List,
	UserNotes,
	Business
} from '@/modules/dashboard/dashboardTypes';
import loadingAnimation from './saving-note-animation.json';
import ActivityListItem from './ActivityListItem/ActivityListItem';
import './BusinessActivityTab.scss';

interface Props {
	openDeleteNoteModal: () => void;
	openOnEmail: (b: Business, e: string) => void;
	onSelectTab: (tabValue: string) => void;
}

const BusinessActivityTab = ({
	openDeleteNoteModal,
	openOnEmail,
	onSelectTab
}: Props): JSX.Element => {
	const [
		selectedList,
		lists,
		selectedBusiness,
		activitiesSortedByList,
		searchedActivitiesByList,
		activitiesLoading,
		activityNotesLoading,
		activityNotesSuccess,
		activityNotesFailure,
		user,
		openBusinessToNote,
		clickDeleteNote
	] = useSelector(
		(state: RootState) => [
			state.dashboard.getIn(['lists', 'selected']).toJS(),
			state.dashboard.getIn(['lists', 'data']).toJS(),
			state.dashboard.getIn(['businesses', 'selected']).toJS(),
			state.dashboard.getIn(['activities', 'sortedByList']).toJS(),
			state.dashboard.getIn(['activities', 'searchedByList']).toJS(),
			state.dashboard.getIn(['activities', 'isLoading']),
			state.dashboard.getIn(['activityNotes', 'isLoading']),
			state.dashboard.getIn(['activityNotes', 'success']),
			state.dashboard.getIn(['activityNotes', 'failure']),
			state.user.getIn(['authorize', 'user']).toJS(),
			state.dashboard.getIn(['businesses', 'openToNote']),
			state.dashboard.getIn(['notes', 'clickDeleteNote'])
		],
		shallowEqual
	);
	const dispatch = useDispatch();
	const [maxHeight, setMaxHeight] = useState<number>();
	const [showValidation, setShowValidation] = useState<boolean>(false);
	const [composeDropdownOpen, setComposeDropdownOpen] =
		useState<boolean>(false);
	const [composeDropdownLists, setComposeDropdownLists] = useState<List[]>(
		[]
	);
	const [selectedComposeDropdownList, setSelectedComposeDropdownList] =
		useState<List>(selectedList);
	const [hideLeadSuggestion, setHideLeadSuggestion] = useState(false);
	const [leadUpdate, setLeadUpdate] = useState(false);
	const [showLeadFollowUp, setShowLeadFollowUp] = useState(false);
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [tempSearchTerm, setTempSearchTerm] = useState<string>(''); // For the input field
	const [showComposeNote, setShowComposeNote] = useState<boolean>(false);
	const [note, setNote] = useState<string>('');
	const inputWrapperRef = useRef(null);
	const validateDebounce = useRef<ReturnType<typeof setTimeout>>();
	const composeNoteHeaderRef = useRef(null);
	const { pathname } = useLocation();

	const closeComposeDropdown = e => {
		if (!composeNoteHeaderRef?.current?.contains(e.target)) {
			setComposeDropdownOpen(false);
		}
	};

	useEffect(() => {
		window.addEventListener('click', closeComposeDropdown);

		return () => window.removeEventListener('click', closeComposeDropdown);
	}, []);

	const calcMaxHeight = () => {
		const e = inputWrapperRef?.current?.getBoundingClientRect();

		if (e?.top < 0) {
			return Math.abs(e?.top) + window.innerHeight - 60;
		}

		return window.innerHeight - e?.top - 60;
	};

	const getContactedOverOneHour = () => {
		if (
			selectedBusiness.lastContact &&
			Object.keys(selectedBusiness.lastContact).length > 0
		) {
			let temp = 0;
			const lastContactArr = Object.keys(
				selectedBusiness.lastContact
			).filter(key => {
				const epochDate = new Date(
					selectedBusiness.lastContact[key]['date']
				).valueOf();
				if (temp < epochDate) {
					temp = epochDate;
				}
			});
			if (temp > 0) {
				const lastDate = new Date(temp).valueOf();
				const curr = new Date().getTime();
				const diff = parseInt(curr) - parseInt(lastDate);
				return diff / 1000 / 60 / 60 > 1;
			}
			return false;
		}
		return false;
	};

	const showLeadSuggestion = () => {
		if (
			selectedBusiness.isContacted &&
			(!selectedBusiness.lead || leadUpdate) &&
			(selectedBusiness.lead !== false || leadUpdate) &&
			!hideLeadSuggestion &&
			getContactedOverOneHour()
		) {
			return true;
		}
		return false;
	};

	const activateLeadButton = (buttonEl, markedAsLead) => {
		buttonEl.classList.add('lead-button-activated');
		setLeadUpdate(true);
		if (markedAsLead) {
			setTimeout(() => {
				buttonEl.classList.remove('lead-button-activated');
				setShowLeadFollowUp(true);
				setHideLeadSuggestion(true);
				setLeadUpdate(false);
			}, 2000);
			setTimeout(() => {
				setShowLeadFollowUp(false);
			}, 12000);
		} else {
			setTimeout(() => {
				buttonEl.classList.remove('lead-button-activated');
				setShowLeadFollowUp(true);
				setHideLeadSuggestion(true);
				setLeadUpdate(false);
			}, 2000);
			setTimeout(() => {
				setShowLeadFollowUp(false);
			}, 12000);
		}
	};

	const hideAllLeadBoxes = () => {
		setHideLeadSuggestion(true);
		setShowLeadFollowUp(false);
	};

	useEffect(() => {
		setHideLeadSuggestion(false);
	}, [selectedBusiness?._id, selectedBusiness?.businessId]);

	const setBusinessNotesDispatch = ({
		businessId,
		businessName,
		notes,
		listId
	}) =>
		dispatch(
			dashboardUpdateBusinessNotesRequest({
				businessId,
				businessName,
				notes,
				listId
			})
		);

	const getActivitiesDispatch = useCallback(
		({ businessId }) => {
			dispatch(dashboardGetActivitiesRequest({ businessId }));
		},
		[dispatch]
	);

	const createActivityNoteDispatch = ({ businessId, listId, text }) =>
		dispatch(dashboardCreateActivityNote({ businessId, listId, text }));

	const createActivityNoteClearDispatch = () =>
		dispatch(dashboardCreateActivityNoteClear());

	const activitySearchDispatch = (term: string) =>
		dispatch(dashboardActivitySearch(term));

	const getBusinessCloseNote = () =>
		dispatch(dashboardGetBusinessCloseNote());

	const clickDeleteNoteClear = () =>
		dispatch(dashboardClickDeleteNoteClear());

	useEffect(() => {
		if (clickDeleteNote) {
			setNote('');
			setShowComposeNote(false);
			clickDeleteNoteClear();
		}
	}, [clickDeleteNote]);

	useEffect(() => {
		if (openBusinessToNote && !showComposeNote) {
			setShowComposeNote(true);
			getBusinessCloseNote();
		}
	}, [openBusinessToNote]);

	useEffect(() => {
		if (activityNotesSuccess) {
			setTimeout(() => {
				createActivityNoteClearDispatch();
				setShowComposeNote(false);
				setNote('');

				setTimeout(() => {
					getActivitiesDispatch({
						businessId: selectedBusiness?.businessId
							? selectedBusiness.businessId
							: selectedBusiness?._id
								? selectedBusiness._id
								: selectedBusiness.id
					});
				}, 500);
			}, 2000);
		}

		if (activityNotesFailure) {
			setTimeout(() => {
				createActivityNoteClearDispatch();
			}, 5000);
		}
	}, [activityNotesSuccess]);

	useEffect(() => {
		return () => {
			createActivityNoteClearDispatch();
			setShowComposeNote(false);
			setNote('');
		};
	}, []);

	useEffect(() => {
		const businessId =
			selectedBusiness.businessId ||
			selectedBusiness._id ||
			selectedBusiness.id;

		if (businessId) {
			getActivitiesDispatch({ businessId });
		}
	}, [
		selectedBusiness?.businessId,
		selectedBusiness?._id,
		selectedBusiness?.id,
		getActivitiesDispatch
	]);

	useEffect(() => {
		const listsArr = [];

		lists.forEach(list => {
			if (
				list.businessIds.find(id => {
					return (
						id === selectedBusiness?.businessId ||
						id === selectedBusiness?._id
					);
				})
			) {
				listsArr.push(list);
			}
		});

		setComposeDropdownLists(listsArr);
	}, []);

	useEffect(() => {}, [searchedActivitiesByList]);

	const saveNote = (
		businessId: string,
		businessName: string,
		notes: UserNotes,
		listId: string | undefined
	) => {
		const dataObj = {
			businessId,
			businessName,
			notes,
			listId: listId ?? selectedBusiness.listId
		};

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User saved a note`,
				properties: {
					businessId,
					note: notes
				}
			});
		}

		setBusinessNotesDispatch(dataObj);
	};

	const toggleLead = (value: boolean | undefined | null) => {
		if (saveNote) {
			const businessId = selectedBusiness?.businessId
				? selectedBusiness.businessId
				: selectedBusiness?._id;
			const currentNoteEl = document.getElementsByClassName(
				'business-notes--text'
			)[0];
			let currentNoteVal;
			if (currentNoteEl) {
				currentNoteVal = currentNoteEl.value;
			}
			saveNote(
				businessId as string,
				selectedBusiness.name,
				{
					isContacted: selectedBusiness?.isContacted,
					lead: value,
					note: selectedBusiness?.note ?? '',
					currentNoteText: currentNoteVal,
					contactType: selectedBusiness?.contactType,
					userAddedData: selectedBusiness.userAddedData ?? ''
				} as UserNotes,
				selectedComposeDropdownList.id
			);
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: 'Lead was changed from the business preview',
					properties: {
						address: selectedBusiness?.address,
						id: selectedBusiness?.businessId
							? selectedBusiness.businessId
							: selectedBusiness?._id,
						name: selectedBusiness?.name
					}
				});
			}
			if (value === undefined) {
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: `Lead was set to neutral${pathname === '/communication-center' ? ' - Communication Center page' : ''}`,
						properties: {
							address: selectedBusiness?.address,
							id: selectedBusiness?.businessId
								? selectedBusiness.businessId
								: selectedBusiness?._id,
							name: selectedBusiness?.name
						}
					});
				}
			}
			if (value === true) {
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: `Lead was added${pathname === '/communication-center' ? ' - Communication Center page' : ''}`,
						properties: {
							address: selectedBusiness?.address,
							id: selectedBusiness?.businessId
								? selectedBusiness.businessId
								: selectedBusiness?._id,
							name: selectedBusiness?.name
						}
					});
				}
			}
			if (value === false) {
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: `Lead was removed${pathname === '/communication-center' ? ' - Communication Center page' : ''}`,
						properties: {
							address: selectedBusiness?.address,
							id: selectedBusiness?.businessId
								? selectedBusiness.businessId
								: selectedBusiness?._id,
							name: selectedBusiness?.name
						}
					});
				}
			}
		}
	};

	const checkEmails = () => {
		if (
			!selectedBusiness?.email &&
			!selectedBusiness?.facebook?.email &&
			!selectedBusiness?.web?.email
		) {
			if (
				!selectedBusiness?.userAddedData ||
				selectedBusiness?.userAddedData.length === 0
			) {
				return false;
			}

			for (let i = 0; i < selectedBusiness.userAddedData.length; i++) {
				if (selectedBusiness.userAddedData[i].type === 'email') {
					return true;
				}
			}
		}

		return true;
	};

	return (
		<div
			className="activity-tab"
			onClick={() => {
				if (showComposeNote) {
					setMaxHeight(calcMaxHeight());
				}
			}}
		>
			{activitiesLoading && (
				<>
					<div className="skeleton-div-one">
						<Skeleton
							sx={{
								height: '90px',
								bgcolor: '#505050',
								borderRadius: '10px'
							}}
						/>
						<Skeleton
							sx={{
								height: '70px',
								width: '128px',
								bgcolor: '#505050',
								borderRadius: '30px',
								alignSelf: 'center'
							}}
						/>
					</div>
					<div className="skeleton-div-two">
						<Skeleton
							sx={{
								bgcolor: '#505050',
								borderRadius: '10px',
								height: '870px',
								marginTop: '-175px'
							}}
						/>
						<div className="skeleton-div-two-inner">
							<div className="skeleton-div-two-inner-inner-1">
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '50%',
										height: '70px',
										width: '43px'
									}}
								/>
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '13px',
										height: '33px'
									}}
								/>
							</div>
							<div className="skeleton-div-two-inner-inner-2">
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '50%',
										height: '70px',
										width: '43px'
									}}
								/>
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '13px',
										height: '33px'
									}}
								/>
							</div>
							<div className="skeleton-div-two-inner-inner-3">
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '50%',
										height: '70px',
										width: '43px'
									}}
								/>
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '13px',
										height: '33px'
									}}
								/>
							</div>
							<div
								className="skeleton-div-two-inner-inner-1"
								style={{ marginTop: '-10px' }}
							>
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '50%',
										height: '70px',
										width: '43px'
									}}
								/>
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '13px',
										height: '33px'
									}}
								/>
							</div>
							<div className="skeleton-div-two-inner-inner-2">
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '50%',
										height: '70px',
										width: '43px'
									}}
								/>
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '13px',
										height: '33px'
									}}
								/>
							</div>
							<div className="skeleton-div-two-inner-inner-3">
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '50%',
										height: '70px',
										width: '43px'
									}}
								/>
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '13px',
										height: '33px'
									}}
								/>
							</div>
							<div
								className="skeleton-div-two-inner-inner-1"
								style={{ marginTop: '-10px' }}
							>
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '50%',
										height: '70px',
										width: '43px'
									}}
								/>
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '13px',
										height: '33px'
									}}
								/>
							</div>
							<div className="skeleton-div-two-inner-inner-2">
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '50%',
										height: '70px',
										width: '43px'
									}}
								/>
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '13px',
										height: '33px'
									}}
								/>
							</div>
							<div className="skeleton-div-two-inner-inner-3">
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '50%',
										height: '70px',
										width: '43px'
									}}
								/>
								<Skeleton
									sx={{
										bgcolor: '#505050',
										borderRadius: '13px',
										height: '33px'
									}}
								/>
							</div>
						</div>
					</div>
				</>
			)}
			{!activitiesLoading && (
				<>
					{showLeadSuggestion() ? (
						<>
							<div className="activity-tab-lead-suggestion-box">
								<div className="activity-tab-lead-text-box">
									<div className="activity-tab-lead-title">
										Did this result in a lead?
									</div>
									{selectedList && (
										<div className="activity-tab-lead-list-name">
											{selectedList.name}
										</div>
									)}
								</div>
								<div className="activity-tab-lead-buttons-box">
									<div
										className="activity-tab-lead-button"
										onClick={evt => {
											activateLeadButton(
												evt.currentTarget,
												true
											);
											toggleLead(true);
										}}
									>
										<CheckmarkIcon className="activity-tab-lead-button-check activity-tab-lead-button-check-white" />
										<CheckmarkIconBlack className="activity-tab-lead-button-check activity-tab-lead-button-check-black" />
										<span>Yes</span>
									</div>
									<div
										className="activity-tab-lead-button activity-tab-lead-no-button"
										onClick={evt => {
											activateLeadButton(
												evt.currentTarget,
												false
											);
											toggleLead(false);
										}}
									>
										<XIcon className="activity-tab-lead-button-check activity-tab-lead-button-check-white" />
										<XIconBlack className="activity-tab-lead-button-check activity-tab-lead-button-check-black" />
										<span>No</span>
									</div>
									<div
										className="activity-tab-lead-ask-later"
										onClick={() => {
											setHideLeadSuggestion(true);
										}}
									>
										Ask me later
									</div>
								</div>
								<div className="activity-tab-lead-background" />
							</div>
						</>
					) : (
						''
					)}
					{showLeadFollowUp && selectedBusiness.lead === true ? (
						<div className="activity-tab-lead-follow-up-holder">
							<div className="activity-tab-lead-follow-up-holder-bg" />
							<div className="activity-tab-lead-follow-up-checkholder">
								<Checkmark className="activity-tab-lead-follow-up-check" />
							</div>
							<div className="activity-tab-lead-follow-up-title">
								Marked as Lead in {selectedList.name}
							</div>
							<div
								className="activity-tab-lead-follow-up-undo"
								onClick={() => {
									toggleLead(undefined);
									hideAllLeadBoxes();
								}}
							>
								Undo this action
							</div>
						</div>
					) : (
						''
					)}
					{showLeadFollowUp && selectedBusiness.lead === false ? (
						<div className="activity-tab-lead-follow-up-holder">
							<div className="activity-tab-lead-follow-up-holder-bg" />
							<div className="activity-tab-lead-follow-up-checkholder-negative">
								<XIconBlack className="activity-tab-lead-follow-up-x" />
							</div>
							<div className="activity-tab-lead-follow-up-title">
								Marked as not a Lead in {selectedList.name}
							</div>
							<div
								className="activity-tab-lead-follow-up-undo"
								onClick={() => {
									toggleLead(undefined);
									hideAllLeadBoxes();
								}}
							>
								Undo this action
							</div>
						</div>
					) : (
						''
					)}
					<div className="activity-tab-header">
						{activitiesSortedByList.length > 0 && (
							<label
								className="activity-tab-search-label"
								htmlFor="activity-tab-search-input"
							>
								<input
									id="activity-tab-search-input"
									className="activity-tab-search-input"
									placeholder="Search"
									autoComplete="off"
									onKeyDown={e => {
										if (e.key === 'Enter') {
											setSearchTerm(tempSearchTerm); // Update the active search term
											activitySearchDispatch(
												tempSearchTerm
											);
										}
									}}
									onChange={e =>
										setTempSearchTerm(e.target.value)
									} // Update temporary input state
									value={tempSearchTerm}
								/>
								<SearchIconGold
									className="activity-tab-search-icon"
									onClick={() => {
										setSearchTerm(tempSearchTerm); // Update the active search term
										activitySearchDispatch(tempSearchTerm);
									}}
								/>
							</label>
						)}
						<div
							className={`activity-add-note-button ${showComposeNote ? 'hide' : ''}`}
							onClick={() => setShowComposeNote(!showComposeNote)}
						>
							<span className="activity-add-note-button-inner-div">
								<PlusGold />
								Add Note
							</span>
						</div>
					</div>
					{activityNotesLoading && (
						<div className="compose-note-loading">
							<span className="compose-note-loading-text">
								Saving note...
							</span>
							<span className="compose-note-loading-filler">
								Filler text
							</span>
							<Lottie
								animationData={loadingAnimation}
								loop
								style={{ width: 250, height: 250 }}
							/>
						</div>
					)}
					{activityNotesSuccess && (
						<div className="compose-note-success">
							<CheckmarkIconBlack width={24} />
							Note saved
						</div>
					)}
					{showComposeNote &&
						!activityNotesLoading &&
						!activityNotesSuccess && (
							<div className="compose-note">
								<div className="compose-note-input-div">
									{activityNotesFailure && (
										<div className="compose-note-failure">
											<ExclamationMarkBlack />
											Note failed to save
										</div>
									)}
									<>
										<div
											className={`compose-note-input-header ${activityNotesFailure ? 'failure' : ''}`}
										>
											<span
												className="compose-note-input-header-left"
												onClick={() =>
													setComposeDropdownOpen(
														!composeDropdownOpen
													)
												}
												ref={composeNoteHeaderRef}
											>
												<span className="compose-note-input-header-left-text">
													New Note
													{lists.length &&
													selectedComposeDropdownList?.name
														? ` - ${selectedComposeDropdownList.name}`
														: ''}
												</span>
												{composeDropdownLists.length >=
													1 &&
													Object.keys(selectedList)
														.length === 0 && (
														<DropDownGold
															className={`compose-note-input-header-left-icon ${composeDropdownOpen ? 'flipped' : ''}`}
														/>
													)}
											</span>
											<span
												className="compose-note-input-header-right"
												onClick={openDeleteNoteModal}
											>
												<TrashNew />
												Discard
											</span>
										</div>
										<div
											className="compose-note-input-wrapper"
											ref={inputWrapperRef}
											style={{
												maxHeight: `${maxHeight}px`
											}}
										>
											<span
												className={`compose-note-input-replica ${showValidation ? 'validation' : ''}`}
											>
												{note}
											</span>
											<textarea
												id="message"
												className="compose-note-input"
												placeholder="Type note here"
												onChange={e => {
													setNote(e.target.value);

													if (
														e.target.value.trim()
															.length === 0 &&
														!showValidation
													) {
														if (
															validateDebounce.current
														) {
															clearTimeout(
																validateDebounce.current
															);
														}

														setShowValidation(true);

														validateDebounce.current =
															setTimeout(() => {
																setShowValidation(
																	false
																);
															}, 3000);
													}

													if (
														e.target.value.trim()
															.length > 0 &&
														showValidation
													) {
														if (
															validateDebounce.current
														) {
															clearTimeout(
																validateDebounce.current
															);
														}

														setShowValidation(
															false
														);
													}
												}}
												value={note}
											/>
										</div>

										<label
											htmlFor="message"
											className={`compose-note-input-validation ${showValidation ? 'show' : ''}`}
										>
											<ExclamationMarkRed className="compose-note-input-validation-icon" />
											Note empty
										</label>
										<button
											className="compose-note-input-button"
											type="button"
											onClick={() => {
												if (
													note.trim().length === 0 &&
													!showValidation
												) {
													if (
														validateDebounce.current
													) {
														clearTimeout(
															validateDebounce.current
														);
													}

													setShowValidation(true);

													validateDebounce.current =
														setTimeout(() => {
															setShowValidation(
																false
															);
														}, 3000);
												}

												if (note.trim().length > 0) {
													if (
														validateDebounce.current
													) {
														clearTimeout(
															validateDebounce.current
														);
													}

													if (showValidation) {
														setShowValidation(
															false
														);
													}

													createActivityNoteDispatch({
														businessId:
															selectedBusiness?.businessId
																? selectedBusiness.businessId
																: selectedBusiness?._id
																	? selectedBusiness._id
																	: selectedBusiness.id,
														listId: selectedComposeDropdownList.id,
														text: note
													});
												}
											}}
										>
											Save Note
										</button>
									</>
								</div>
								{composeDropdownLists.length >= 1 &&
									Object.keys(selectedList).length === 0 && (
										<ul
											className={`compose-dropdown-list ${composeDropdownOpen ? '' : 'hide'}`}
										>
											{composeDropdownLists.map(
												(list, i) => (
													<li
														key={list.id || i}
														className={`compose-dropdown-list-element ${list.id === selectedComposeDropdownList.id ? 'selected' : ''}`}
														onClick={() =>
															setSelectedComposeDropdownList(
																list
															)
														}
													>
														{list.name}
													</li>
												)
											)}
										</ul>
									)}
							</div>
						)}
					<div
						className={`activity-list-container ${activitiesSortedByList.length === 0 ? 'grid' : ''}`}
					>
						{composeDropdownLists.length === 0 &&
							activitiesSortedByList.length > 0 && (
								<span className="activity-list-no-list-text">
									This business is not currently in any List.
									Activity from Lists the business was removed
									from will still appear.
								</span>
							)}
						{activitiesSortedByList.length > 0 &&
							searchedActivitiesByList.length === 0 &&
							searchTerm === '' && (
								<>
									{activitiesSortedByList.map(
										(item, index) => {
											const matchedList = lists.find(
												(list: List) =>
													list.name === item[0]
											);

											if (matchedList) {
												return (
													<ActivityListItem
														list={matchedList}
														activities={item[1]}
														index={index}
													/>
												);
											}

											return (
												<ActivityListItem
													list={{
														id: '123',
														name: 'General'
													}}
													activities={item[1]}
													index={index}
												/>
											);
										}
									)}
								</>
							)}
						{searchedActivitiesByList.length > 0 && (
							<>
								{searchedActivitiesByList.map((item, index) => {
									const matchedList = lists.find(
										(list: List) => list.name === item[0]
									);

									if (matchedList) {
										return (
											<ActivityListItem
												list={matchedList}
												activities={item[1]}
												index={index}
											/>
										);
									}

									return (
										<ActivityListItem
											list={{
												id: '123',
												name: 'General'
											}}
											activities={item[1]}
											index={index}
										/>
									);
								})}
							</>
						)}
						{activitiesSortedByList.length === 0 &&
							checkEmails() && (
								<div className="activity-list-container-empty">
									<ActivityListEmpty />
									<span className="activity-list-container-empty-text-1">
										Activity with this business will show
										here.
									</span>
									<span className="activity-list-container-empty-text-2">
										Get started by sending an email to{' '}
										{selectedBusiness.name}
									</span>
									<button
										type="button"
										className="activity-list-container-empty-button"
										onClick={() => {
											if (selectedBusiness?.email) {
												openOnEmail({
													business: selectedBusiness,
													email: selectedBusiness?.email
												});
											} else if (
												selectedBusiness?.facebook
													?.email
											) {
												openOnEmail({
													business: selectedBusiness,
													email: selectedBusiness
														?.facebook?.email
												});
											} else if (
												selectedBusiness?.web?.email
											) {
												openOnEmail({
													business: selectedBusiness,
													email: selectedBusiness?.web
														?.email
												});
											} else if (
												selectedBusiness?.userAddedData
											) {
												for (
													let i = 0;
													i <
													selectedBusiness
														.userAddedData.length;
													i++
												) {
													if (
														selectedBusiness
															.userAddedData[i]
															.type === 'email'
													) {
														openOnEmail({
															business:
																selectedBusiness,
															email: selectedBusiness
																.userAddedData[
																i
															].data
														});
														break;
													}
												}
											}
										}}
									>
										<Compose className="activity-list-container-empty-button-icon" />
										Compose Message
									</button>
								</div>
							)}
						{activitiesSortedByList.length === 0 &&
							!checkEmails() && (
								<div className="activity-list-container-empty">
									<ActivityListEmpty />
									<span className="activity-list-container-empty-text-1">
										Activity with this business will show
										here.
									</span>
									<span className="activity-list-container-empty-text-2">
										Get started by contacting{' '}
										{selectedBusiness.name}
									</span>
									<button
										type="button"
										className="activity-list-container-empty-button"
										onClick={() => onSelectTab('info')}
									>
										<Megaphone className="activity-list-container-empty-button-icon" />
										Contact Business
									</button>
								</div>
							)}
					</div>
				</>
			)}
		</div>
	);
};

export default BusinessActivityTab;
